import { useQueryClient } from '@tanstack/react-query'
import { api } from '../http'
import { buildJsonApiParams } from '../jsonApi'
import { queryKeys } from '../queryKeys'
import { useApiQuery, useApiMutation } from './_base'

/** GET /tags */
export function useTagList(params = {}, options = {}) {
    const apiParams = buildJsonApiParams(params)
    return useApiQuery({
        queryKey: queryKeys.tags.list(apiParams),
        url: '/tags',
        params: apiParams,
        options,
    })
}

/** GET /tags/{id} */
export function useTag(id, params = {}, options = {}) {
    const apiParams = buildJsonApiParams(params)
    return useApiQuery({
        queryKey: queryKeys.tags.detail(id, apiParams),
        url: `/tags/${id}`,
        params: apiParams,
        options: { enabled: !!id, ...options },
    })
}

/** POST /tags */
export function useCreateTag() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['tags', 'create'],
        mutationFn: (payload) => api.post('/tags', {}, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.tags.all })
        },
    })
}

/** PUT /tags/{id} */
export function useUpdateTag() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['tags', 'update'],
        mutationFn: ({ id, ...payload }) =>
            api.put(`/tags/${id}`, {}, payload),
        onSuccess: (_d, { id }) => {
            qc.invalidateQueries({ queryKey: queryKeys.tags.detail(id) })
            qc.invalidateQueries({ queryKey: queryKeys.tags.lists() })
        },
    })
}

/**
 * Optimistic toggle for the active flag on a tag — used heavily in tag
 * filtering UI. Snapshots all cached tag lists, patches the toggled row, and
 * rolls back on failure.
 */
export function useToggleTagActive() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['tags', 'toggleActive'],
        mutationFn: ({ id, active }) =>
            api.put(`/tags/${id}`, {}, { active: active ? 1 : 0 }),
        onMutate: async ({ id, active }) => {
            await qc.cancelQueries({ queryKey: queryKeys.tags.lists() })
            const snapshots = qc.getQueriesData({
                queryKey: queryKeys.tags.lists(),
            })
            qc.setQueriesData(
                { queryKey: queryKeys.tags.lists() },
                (old) => {
                    if (!old?.data) return old
                    return {
                        ...old,
                        data: old.data.map((t) =>
                            String(t.id) === String(id)
                                ? { ...t, active: active ? 1 : 0 }
                                : t
                        ),
                    }
                }
            )
            return { snapshots }
        },
        onError: (_e, _v, ctx) => {
            ctx?.snapshots?.forEach(([key, data]) =>
                qc.setQueryData(key, data)
            )
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: queryKeys.tags.all })
        },
    })
}

/** DELETE /tags/{id} */
export function useDeleteTag() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['tags', 'delete'],
        mutationFn: (id) => api.delete(`/tags/${id}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.tags.all })
            qc.invalidateQueries({ queryKey: queryKeys.postGroups.all })
        },
    })
}
